import * as React from "react";
import { useHistory, useParams } from "react-router";
import { URLS, useAccountSettings, useSocialCoachSession } from "@/utils";
import { DigestEmails } from "@/containers/Coach/DigestEmails";
import { VideoPageThemes } from "@/containers/VideoPages/VideoPageThemes";
import { PageWrapper } from "@/components/PageWrapper";
import { StickersTab } from "@/components/Stickers";
import { CoachHashtagsTab } from "@/components/CoachHashtagsTab";
import CoachProfile from "../CoachProfile";
import { ApiKeys } from "@/containers/ApiKeys";
import { WebComponents } from "@/interfaces";
import { PlatformSettingsTab } from "./PlatformSettingsTab";
import { CoachSocialProfilesTab } from "./CoachSocialProfilesTab";
import { Card, Input, Button } from "@/components/ui";
import {
  Building2,
  SlidersHorizontal,
  Share2,
  Smile,
  Hash,
  Mail,
  Palette,
  Key,
  ChevronRight,
  Search,
} from "lucide-react";

const SettingsFC: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ tab: string | undefined }>();
  const { hasReadPermission } = useAccountSettings();
  const { isMainCoach } = useSocialCoachSession();
  const [searchQuery, setSearchQuery] = React.useState("");

  const menuItems: {
    title: string;
    activeSection: string;
    component: any;
    icon: React.ReactNode;
    description: string;
    tags: string[];
  }[] = [];

  if (isMainCoach) {
    menuItems.push({
      title: "Company Info",
      activeSection: "company-info",
      component: CoachProfile,
      icon: <Building2 className="size-5 text-primary" />,
      description:
        "Update your brand profile image and business details. Configure support routing preferences to direct player help tickets internally or to the SocialCoach support team.",
      tags: [
        "password",
        "security",
        "login",
        "reset",
        "email",
        "phone",
        "support",
        "logo",
        "avatar",
        "photo",
        "first name",
        "last name",
        "title",
        "company",
        "agency",
        "support routing",
        "credentials",
      ],
    });
  }

  if (hasReadPermission(WebComponents.APP_SETTINGS)) {
    menuItems.push({
      title: "Platform Settings",
      activeSection: "platform-settings",
      component: PlatformSettingsTab,
      icon: <SlidersHorizontal className="size-5 text-primary" />,
      description:
        "Manage global permissions for player capabilities, available social networks, and content customization preferences.",
      tags: [
        "permissions",
        "toggles",
        "scheduling",
        "allow scheduling",
        "prompt permission",
        "capabilities",
        "features",
        "privileges",
        "active networks",
        "facebook toggle",
        "tiktok",
        "caption editing",
        "allow post",
        "player permissions",
        "player card",
        "reputation",
        "twitter",
        "x",
        "zillow",
        "google",
        "zillow",
        "stickers",
        "hashtags",
        "video pages",
        "calendar",
        "disclaimer",
        "notification",
        "compliance",
      ],
    });
    menuItems.push({
      title: "Managed Social Connections",
      activeSection: "managed-social-connections",
      component: CoachSocialProfilesTab,
      icon: <Share2 className="size-5 text-primary" />,
      description:
        "Link admin-level social network accounts and bulk-distribute specific child connections, listings, or location channels directly to your players.",
      tags: [
        "facebook page",
        "instagram",
        "google business",
        "gmb",
        "locations",
        "linkedin organization",
        "parent connections",
        "bulk assign",
        "distribution",
        "child profiles",
        "csv import",
        "staged queue",
        "audit",
        "expiration warning",
        "unlinked",
        "disconnect",
      ],
    });
    menuItems.push({
      title: "Custom Stickers",
      activeSection: "stickers",
      component: StickersTab,
      icon: <Smile className="size-5 text-primary" />,
      description:
        "Upload and organize branded transparent stickers, overlays, and logos that players can use to personalize their prompts.",
      tags: ["stickers", "transparent overlays", "logos", "watermark", "graphics", "png", "custom graphics"],
    });
    menuItems.push({
      title: "Default Hashtags",
      activeSection: "hashtags",
      component: CoachHashtagsTab,
      icon: <Hash className="size-5 text-primary" />,
      description: "Pre-configure global hashtag rules to automatically append relevant tags to every post.",
      tags: ["hashtags", "default tags", "automatic hashtags", "templates", "post tags", "brand tags", "categories"],
    });
  }

  if (hasReadPermission(WebComponents.APP_SETTINGS)) {
    menuItems.push({
      title: "Branded Page Themes",
      activeSection: "mvp",
      component: VideoPageThemes,
      icon: <Palette className="size-5 text-primary" />,
      description:
        "Design unified styles for Video Pages, Forms, and Embedded Widgets. Customize colors, logos, and disclaimers for each region and branch.",
      tags: [
        "themes",
        "colors",
        "logos",
        "disclaimers",
        "widgets",
        "forms",
        "landing page",
        "video page",
        "headers",
        "footers",
        "styles",
        "segment defaults",
        "branch themes",
        "eh logo",
      ],
    });
  }

  if (hasReadPermission(WebComponents.DIGEST_EMAIL)) {
    menuItems.push({
      title: "Digest Emails",
      activeSection: "digest-emails",
      component: DigestEmails,
      icon: <Mail className="size-5 text-primary" />,
      description:
        "Get tactical daily insights, weekly recaps, and monthly strategic summaries delivered straight to your inbox to monitor team performance.",
      tags: [
        "digest",
        "emails",
        "failed posts",
        "post failures",
        "alerts",
        "compliance issues",
        "summaries",
        "flags",
        "notifications",
        "scheduled",
        "daily",
        "weekly",
        "monthly",
        "subscription",
        "performance",
      ],
    });
  }

  if (hasReadPermission(WebComponents.API_KEYS)) {
    menuItems.push({
      title: "Developer APIs",
      activeSection: "api-keys",
      component: ApiKeys,
      icon: <Key className="size-5 text-primary" />,
      description:
        "Generate secure keys and access api documentation. Empower your internal teams to pull performance data, sync records, and build custom automation workflows.",
      tags: [
        "api keys",
        "integrations",
        "automations",
        "api docs",
        "developer documentation",
        "internal dev",
        "webhooks",
        "crm integration",
        "security tokens",
        "data pull",
      ],
    });
  }

  const activeSection = params.tab;
  const activeMenuItem = menuItems.find(item => item.activeSection === activeSection);

  // Filter menu items by search query (matching title, description, or custom search tags!)
  const filteredMenuItems = menuItems.filter(item => {
    const title = item.title.toLowerCase();
    const desc = item.description.toLowerCase();
    const tags = item.tags.map(t => t.toLowerCase());
    const q = searchQuery.toLowerCase();
    return title.includes(q) || desc.includes(q) || tags.some(tag => tag.includes(q));
  });

  const navigateToTab = (sectionId: string) => {
    history.push(URLS.coach.settingsTab.replace(":tab?", sectionId));
  };

  const navigateBackToSettings = () => {
    history.push(URLS.coach.settingsTab.replace("/:tab?", ""));
  };

  if (activeMenuItem) {
    const ActiveComponent = activeMenuItem.component;
    const breadcrumbList = [{ name: "Settings", link: navigateBackToSettings }];

    return (
      <PageWrapper title={activeMenuItem.title} breadcrumbs={breadcrumbList} hideSeparator>
        <div className="w-full">
          <ActiveComponent />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Settings" hideSeparator>
      <div className="w-full flex flex-col gap-6 max-w-6xl">
        {/* Search bar is the first thing on the page (no separate titles/subtitles) */}
        <div className="relative w-full md:w-80 shrink-0 mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search settings..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* Cards Grid */}
        {filteredMenuItems.length === 0 ? (
          <div className="text-center py-20 flex flex-col items-center justify-center gap-2">
            <Search className="size-10 text-muted-foreground opacity-50" />
            <p className="text-sm font-semibold text-muted-foreground m-0">
              No settings found matching &quot;{searchQuery}&quot;
            </p>
            <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="text-primary font-bold">
              Clear search query
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMenuItems.map(item => (
              <Card
                key={`settings-card-${item.activeSection}`}
                onClick={() => navigateToTab(item.activeSection)}
                className="p-5 flex flex-col gap-3 hover:shadow-md hover:border-primary/20 cursor-pointer transition-all border border-gray-200/60 animate-in fade-in zoom-in-95 duration-150"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="font-bold text-base text-foreground m-0">{item.title}</div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed m-0 flex-1">{item.description}</p>
                <div className="flex items-center text-primary font-bold text-xs gap-0.5 mt-2 self-start hover:underline">
                  <span>Manage</span>
                  <ChevronRight className="size-3.5" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export const SettingsSection = SettingsFC;
